import { useTranslation } from 'react-i18next';
import {
  TICKET_STATUS_GROUPS,
  type TicketStatusGroup,
  ticketStatusGroupLabelKey,
} from '@nbh/shared';

/**
 * The lifecycle tabs — New | In Progress | To Verify | Closed — from the one mapping in
 * `@nbh/shared`, so a status the server can return always has a tab it lands in.
 *
 * A four-column grid, never a scrolling strip: a hidden tab is a hidden lifecycle
 * stage. The chip row below may scroll; this row may not. Each tab clears 48px for a
 * gloved thumb, and the brass underline is the same active marker the ticket detail's
 * sections use, so the two screens read as one system.
 */
export function QueueTabs({
  active,
  onSelect,
}: {
  active: TicketStatusGroup;
  onSelect: (group: TicketStatusGroup) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="tablist"
      aria-label={t('queue.tabs')}
      className="grid grid-cols-4 border-b border-slate-800 bg-surface-2"
    >
      {TICKET_STATUS_GROUPS.map((group) => {
        const selected = group === active;
        return (
          <button
            key={group}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(group)}
            className={`min-h-[48px] border-b-2 bg-transparent px-1 text-[13px] font-semibold whitespace-nowrap ${
              selected
                ? 'border-brookfields-gold text-brookfields-gold-light'
                : 'border-transparent text-slate-400'
            }`}
          >
            {t(ticketStatusGroupLabelKey[group])}
          </button>
        );
      })}
    </div>
  );
}
